import React, {useCallback, useEffect, useState} from "react";
import PropTypes from 'prop-types';

import {Box} from "@mui/material";
import { MaskSizer } from 'mask-sizing'
import {
    nextGenApiBaseUrl,
    isNextGenLogsEnabled,
    isNextGenSaveMugshotsEnabled
} from "../../../AppSettings";
import { makeStyles } from "tss-react/mui";
import NoCameraAccessDialog from "../NoCameraAccessDialog";
import { v3_client } from "../../../api/OpenClient";

import {saveMugshot} from "./MugshotSaver";
import QuitScanDialog from "./QuitScanDialog";
import { useNavigate } from "react-router-dom";

import CameraChooser, { canChooseCamera } from "./CameraChooser";
import {SHOW_CAMERA_DENIED_DIALOG_INTERVAL_IN_MILLISECONDS} from "./MugShotV2Config";

import {nextgen_measurements} from "./Util";
import {useTranslation} from "react-i18next";
import {getLangCodeForAudio} from "ui-common/i18n";

/* This component is a stub for the new UX next gen face scan code */
const FaceScannerPage = ({ 
                             setFieldValue,
                             handleSubmit, 
                             handleImageCapture = null,
                             handleComplete = null,
                             requestTransition,
                             faceMeshModel,
                             imageCheckFunction,
                             tokenData,
                             cameraInfoArray,
                             setCameraInfoArray,
                             cameraId,
                             setCameraId,
                             thresholdConfig = null
                        }) => {
    const buttonNavigationHeight = "70px";
    const [languageCode, setLanguageCode] = useState(null);
    const [deniedCameraAccess, setDeniedCameraAccess] = useState(false);
    const [batchLogs, setBatchLogs] = useState(0); // increase each time we send logs

    const [displayQuitScanDialog, setDisplayQuitScanDialog] = useState(false);

    const { t: maskSizingTranslation } = useTranslation("maskSizing.messages");
    const { t: processingTranslation } = useTranslation("pages.MaskSelectorTool.NextGen.postProcessing");
    const cameraLoading = maskSizingTranslation("cameraLoading");

    const [cameraReady, setCameraReady] = useState(!canChooseCamera());

    const navigate = useNavigate();

    const sendScanLogs = (logData) => {
        const requestObject = {
            token: token,
            hme_org_code: hme_org_code,
            hme_location_code: hme_location_code,
            patient_code: patient_code,
            logs: logData, // array of logs
            batch_number: batchLogs
        }
        setBatchLogs((num) => {
            // whenever I tried to set the batch number in the request, it would always be 0...
            requestObject.batch_number = num
            return num + 1
        })
        // We don't verify response of log failures as we don't want any errors to interrupt the flow
        v3_client.post("/mask-surveys/mask-sizing-logs", requestObject).then((response) => {
            // console.log(response)
        }).catch( error => {
            console.error(error);
        });
    }

    const handleOnQuit = () => {
        setDisplayQuitScanDialog(false);
        const timestamp = new Date().toISOString();

        const logData = [{
            timestamp: timestamp,
            level: "info",
            component: "MaskSizer",
            msg: "User quit the scan",
            state: "quitting",
        }];

        sendScanLogs(logData);

        /*
         We have to go back twice because when the back browser button is clicked, the same page will be reloaded
         again since the history has been manipulated by pushState. For more information see:
         https://stackoverflow.com/questions/55966533/show-alert-on-browser-back-button-event-on-react-js
         */
        navigate(-2);
    }

    const handleClose = () => {
        window.history.pushState(null, null, window.location.pathname);
        setDisplayQuitScanDialog(false);
    }

    const onBackButtonEvent = (e) => {
        e.preventDefault();
        setDisplayQuitScanDialog(true);
    }

    const fetchLanguageCode = useCallback(async () => {
        const lang = await getLangCodeForAudio();
        setLanguageCode(lang);
    }, []);

    useEffect(() => {
        // Start with a clean history state, allows us to maintain consistency in the navigation behavior
        window.history.pushState(null, null, window.location.pathname);
        window.addEventListener('popstate', onBackButtonEvent);
        return () => {
            window.removeEventListener('popstate', onBackButtonEvent);
        };
    }, []);

    useEffect(() => {
        fetchLanguageCode();
    }, [fetchLanguageCode]);

    const { token, hme_org_code, hme_location_code, patient_code } = tokenData;

    const localStyles = makeStyles()(() => ({
        background: {
            background: "#001727",
            width: "100svw",
            position: "relative",
            left: "calc(50% - 50svw)"
        },

        loaderBackground: {
            backgroundColor: "#001727",
            height: "100svh",
        },

        content: {
            height: `calc(100% - ${buttonNavigationHeight})`,
            padding: "0 20px",
            position: "relative",
            "@media only screen and (max-width: 639px)": {
                minWidth: "100%",
                maxWidth: "100%",
                left: "calc(50% - 50svw)",
            },
            "@media only screen and (min-width: 640px)": {
                minWidth: "640px",
                maxWidth: "640px",
                left: "calc(50svw - 320px)",
            }
        }
    }));

    const {classes: localClasses} = localStyles();

    const [retryWebcam, setRetryWebcam] = useState(true);
    const [retryWebcamCount, setRetryWebcamCount] = useState(0);

    const hideDialog = () => {
        setRetryWebcamCount(prev => prev + 1);
        setDeniedCameraAccess(false);
        setRetryWebcam(false);
        // Re-mount the component
        setTimeout(() => {
            setRetryWebcam(true);
        }, SHOW_CAMERA_DENIED_DIALOG_INTERVAL_IN_MILLISECONDS);
    }

    const CustomLoader = ({ loadingComplete, onLoadWebcam, webcamReady, onStartScan, LoadingAnimation }) => {
        useEffect(() => {
            // Desktop path
            if ( loadingComplete && cameraReady ) {
                onLoadWebcam();
            }

            // Mobile path
            if (!canChooseCamera() && loadingComplete) {
                onLoadWebcam();
            }
        }, [loadingComplete, cameraReady, canChooseCamera])

        useEffect(() => {
            // only start scanning when we are on face scanner page and camera is ready
            if (webcamReady) {
               onStartScan();
            }
        }, [webcamReady])

        return (
            <Box className={localClasses.loaderBackground}>
                {(!loadingComplete) &&(
                    <>
                        {/* Render the inner component passed from the child component */}
                        <LoadingAnimation progress loadingMessage={cameraLoading} />
                    </>
                )}
                {loadingComplete && (
                  <NoCameraAccessDialog
                    open={deniedCameraAccess}
                    handleTryManualMeasure={() => {
                        // This allows us to bypass the quit scan and no access dialogs
                        navigate(`/m/sizing_scan_manual_preamble?token=${token}`, { replace: true });
                    }}
                    handleOKButton={hideDialog}
                    displayManualMeasureLink={retryWebcamCount > 0}
                  />
                )}
            </Box>
        )
    }

    CustomLoader.propTypes = {
        loadingComplete: PropTypes.bool.isRequired,
        onLoadWebcam: PropTypes.func.isRequired,
        webcamReady: PropTypes.bool.isRequired,
        onStartScan: PropTypes.func.isRequired,
        LoadingAnimation: PropTypes.elementType
    }

    const handleFaceMeasurerComplete = useCallback(({ data }) => {

        if (data) {

            let promises = [];

            for (const element of nextgen_measurements) {
                let set_field_promise = setFieldValue(element, data.measurements[element]);
                promises.push(set_field_promise)
            }

            // If MaskSizer completed with a 'data' element, then it was successful.
            Promise.allSettled([

                    ...promises,

                setFieldValue("mugshot_measurements", true),
            ]).then(() => {
                // Sometimes we route to the next page before the lottie completes, so we delay the transition
                setTimeout(() => handleSubmit(), 1000)
            });
        } else {
            // No 'data' element means something went wrong in MaskSizer.
            requestTransition("FAILED")
        }
    }, []);
    
    let handlePostLogData;
    let handleSaveMugshot;

    if (isNextGenLogsEnabled()) {
        handlePostLogData = (logData) => {
            if (logData) {
                sendScanLogs(logData);
            }
        }
    } else {
        handlePostLogData = () => {};
    }

    if (isNextGenSaveMugshotsEnabled()) {
        handleSaveMugshot = (event) => {
            saveMugshot({
                mugshotData: event,
                tokenData
            })
        }
    } else {
        handleSaveMugshot = () => {};
    }

    if(handleImageCapture){
        handleSaveMugshot = (event) => handleImageCapture(event);
    }

    const [localRetryCallback, setLocalRetryCallback] = useState( () => {} )

    const onUserMediaErrorCameraChooser = (e, retryCallback) =>
    {
        setDeniedCameraAccess(true);
        // When saving functions in useState, you need a function that points to a function
        // otherwise the function will be evaluated when it is set.
        if (retryCallback) {
            setLocalRetryCallback( () => () => retryCallback()  )

            // Signal that we want to retry accessing the camera permissions again.
            setTimeout(localRetryCallback, SHOW_CAMERA_DENIED_DIALOG_INTERVAL_IN_MILLISECONDS + 400);
        }
    };

    return (
      <>
          {
              // When a user denies camera access and has to retry we have to re-mount the component
              // to re-activate the camera feed
              retryWebcam ? (
                <>
                    {canChooseCamera() && (
                      <CameraChooser
                        noCameraAccessDialogIsShowing={false}
                        onUserMediaError={onUserMediaErrorCameraChooser}
                        setCameraReady={setCameraReady}
                        setCameraId={setCameraId}
                        cameraId={cameraId}
                        cameraInfoArray={cameraInfoArray}
                        setCameraInfoArray={setCameraInfoArray}
                      />
                    )}

                    <Box className={localClasses.background}>
                        <Box className={localClasses.content}>
                            {languageCode && (
                                <MaskSizer
                                    onComplete={handleComplete ? handleComplete : handleFaceMeasurerComplete}
                                    baseUrl={nextGenApiBaseUrl}
                                    faceMeshModel={faceMeshModel}
                                    imageCheckFunction={ imageCheckFunction }
                                    onUserMediaError={onUserMediaErrorCameraChooser}
                                    loader={CustomLoader}
                                    onLogData={handlePostLogData}
                                    onImageCaptured={handleSaveMugshot}
                                    maskSizingTranslation={maskSizingTranslation}
                                    processingTranslation={processingTranslation}
                                    languageCode={languageCode}
                                    thresholdConfig={thresholdConfig}
                                    {
                                        ...{}
                                        /* Use the default settings for mobile phones.  canChooseCamera will
                                                                always return false for phones.  */
                                    }
                                    {...(canChooseCamera() ? { cameraReady: cameraReady } : {})}
                                    {...(canChooseCamera()
                                        ? { cameraChoiceSupported: true }
                                        : {})}
                                    {...(canChooseCamera() ? { cameraId: cameraId } : {})}
                                />
                            )}
                        </Box>

                    </Box>
                </>
              ) : (
                <Box sx={{ height: "100vh" }} className={localClasses.background}></Box>
              )
          }

          <QuitScanDialog
            open={displayQuitScanDialog}
            onClose={handleClose}
            onQuit={handleOnQuit}
          />
      </>
    );
}
FaceScannerPage.propTypes = {
    setFieldValue: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    requestTransition: PropTypes.func.isRequired,
    faceMeshModel: PropTypes.object,
    imageCheckFunction: PropTypes.object,
    thresholdConfig: PropTypes.object,
    tokenData: PropTypes.object.isRequired,
    cameraInfoArray: PropTypes.array.isRequired,
    setCameraInfoArray: PropTypes.func.isRequired,
    setCameraId: PropTypes.func.isRequired,
    cameraId: PropTypes.string,
    handleImageCapture:PropTypes.func,
    handleComplete:PropTypes.func
}
export default FaceScannerPage